import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";

const LINES = ["L'Agilité,", "sans renoncer", "à l'ingénierie."];

const MaskedLine = ({ text, index, gradient }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${gradient ? "grad-text" : ""}`}
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay: 0.25 + index * 0.16, ease: [0.22, 1, 0.36, 1] }}
    >
      {text}
    </motion.span>
  </span>
);

export default function Hero() {
  const { scrollY } = useScroll();
  const yGlyph = useTransform(scrollY, [0, 600], [0, 120]);
  const yCard = useTransform(scrollY, [0, 600], [0, -60]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section className="relative min-h-screen flex items-center noise overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_20%,rgba(37,99,235,0.14),transparent_55%),radial-gradient(ellipse_at_20%_80%,rgba(20,184,166,0.1),transparent_50%)]" />
      <motion.div
        style={{ y: yGlyph }}
        aria-hidden
        className="absolute -right-20 top-1/2 -translate-y-1/2 font-heading font-extrabold text-[38rem] leading-none grad-text opacity-[0.07] select-none pointer-events-none hidden lg:block"
      >
        ×
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20 grid lg:grid-cols-12 gap-12 items-center w-full">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="inline-flex items-center gap-3 border border-white/10 bg-white/[0.03] px-4 py-2 mb-8"
            data-testid="hero-badge"
          >
            <span className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
            <span className="font-mono2 text-[11px] tracking-[0.25em] uppercase text-slate-300">
              Partenariat ASAP Technologies × Reqen
            </span>
            <span className="h-2 w-2 rounded-full bg-teal-400 animate-pulse" />
          </motion.div>

          <h1 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-slate-50 leading-none sm:leading-tight">
            {LINES.map((l, i) => (
              <MaskedLine key={l} text={l} index={i} gradient={i === 2} />
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-7 max-w-xl text-slate-400 text-base leading-relaxed"
          >
            Deux expertises réunies pour des offres packagées autour de l'Agilité : diagnostic,
            amélioration des processus, ingénierie des exigences, conformité et formations
            certifiantes. Pragmatique, mesurable, sans dogme.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <button data-testid="hero-cta-offres" onClick={() => scrollTo("offres")} className="btn-primary">
              Découvrir les offres <ArrowRight size={16} />
            </button>
            <button
              data-testid="hero-cta-contact"
              onClick={() => scrollTo("contact")}
              className="inline-flex items-center gap-2 px-7 py-3.5 text-sm font-semibold text-slate-200 border border-white/15 hover:border-teal-400/50 hover:text-teal-300 transition-colors"
            >
              Prendre contact
            </button>
          </motion.div>
        </div>

        <motion.div style={{ y: yCard }} className="lg:col-span-5 hidden lg:block">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.7, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-3 grad-line opacity-20 blur-2xl" />
            <div className="relative border border-white/10 bg-[#111827]">
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
                alt="Atelier de conseil agile"
                className="w-full h-72 object-cover opacity-80"
              />
              <div className="grid grid-cols-3 divide-x divide-white/10 border-t border-white/10">
                {[
                  ["05", "offres packagées"],
                  ["45+", "ans d'expertise cumulée"],
                  ["03", "certifications préparées"],
                ].map(([n, l]) => (
                  <div key={l} className="p-5">
                    <div className="font-heading text-2xl font-bold grad-text">{n}</div>
                    <div className="mt-1 text-xs text-slate-500">{l}</div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <motion.button
        onClick={() => scrollTo("partenariat")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-500 hover:text-teal-300 transition-colors"
        data-testid="hero-scroll-indicator"
      >
        <ArrowDown size={18} className="animate-bounce" />
      </motion.button>
    </section>
  );
}
